import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAttemptDetails } from '../../api/practiceTests.api';
import './PracticeTestResults.css';

const PracticeTestResults = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [reviewSection, setReviewSection] = useState(null);

  useEffect(() => {
    loadAttempt();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const loadAttempt = async () => {
    try {
      setLoading(true);
      const response = await getAttemptDetails(attemptId);
      setAttempt(response.data);
      if (response.data.sections?.length > 0) {
        setReviewSection(response.data.sections[0].sectionName);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExit = () => {
    navigate('/practice-tests');
  };

  const handleViewHistory = () => {
    navigate('/practice-tests/history');
  };

  if (loading) return <div className="loading">Loading results...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!attempt) return <div className="error-message">Results not found</div>;

  const scorePercentage = attempt.totalScore || 0;
  const scoreColor = scorePercentage >= 70 ? 'green' : scorePercentage >= 50 ? 'orange' : 'red';

  return (
    <div className="practice-test-results">
      {!showReview ? (
        <>
          {/* Results Summary */}
          <div className="results-container">
            <div className="results-header">
              <h1>Test Completed!</h1>
              <p className="test-title">{attempt.testTitle}</p>
            </div>

            <div className="score-display">
              <div className={`score-circle ${scoreColor}`}>
                <span className="score-number">{scorePercentage}%</span>
              </div>
            </div>

            <div className="overall-stats">
              <div className="stat">
                <span className="label">Total Score:</span>
                <span className="value">
                  {attempt.totalCorrect} / {attempt.totalQuestions}
                </span>
              </div>
              <div className="stat">
                <span className="label">Time Taken:</span>
                <span className="value">
                  {Math.floor(attempt.totalDuration / 60)} min{' '}
                  {attempt.totalDuration % 60} sec
                </span>
              </div>
            </div>

            {/* Section Scores */}
            <div className="section-scores">
              <h3>Section Breakdown</h3>
              {attempt.sections?.map(section => {
                const sectionScore = section.score || 0;
                const sectionColor =
                  sectionScore >= 70 ? 'green' : sectionScore >= 50 ? 'orange' : 'red';

                return (
                  <div key={section.sectionName} className="section-score">
                    <div className="section-info">
                      <span className="section-name">{section.displayName}</span>
                      <span className="section-details">
                        {section.correctAnswers} / {section.totalQuestions} correct
                      </span>
                    </div>
                    <div className={`score-bar ${sectionColor}`}>
                      <div
                        className="score-fill"
                        style={{ width: `${sectionScore}%` }}
                      >
                        <span className="score-text">{sectionScore}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="results-actions">
              <button className="btn-review" onClick={() => setShowReview(true)}>
                📋 Review Answers
              </button>
              <button className="btn-history" onClick={handleViewHistory}>
                📊 View History
              </button>
              <button className="btn-exit" onClick={handleExit}>
                ← Back to Tests
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Review Mode */}
          <div className="review-container">
            <div className="review-header">
              <h2>Review Your Answers</h2>
              <button className="btn-close" onClick={() => setShowReview(false)}>
                ✕
              </button>
            </div>

            {/* Section Selector */}
            <div className="review-tabs">
              {attempt.sections?.map(section => (
                <button
                  key={section.sectionName}
                  className={`tab ${reviewSection === section.sectionName ? 'active' : ''}`}
                  onClick={() => setReviewSection(section.sectionName)}
                >
                  {section.displayName}
                </button>
              ))}
            </div>

            {/* Questions Review */}
            <div className="review-questions">
              {attempt.sections
                ?.find(s => s.sectionName === reviewSection)
                ?.answers?.map((answer, index) => {
                  const isCorrect = answer.isCorrect;

                  return (
                    <div
                      key={answer.questionId}
                      className={`review-question ${isCorrect ? 'correct' : 'incorrect'}`}
                    >
                      <div className="question-header">
                        <span className="question-number">Question {index + 1}</span>
                        <span className={`result-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
                          {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      </div>

                      <div className="question-text">{answer.question}</div>

                      <div className="answer-info">
                        <div className="user-answer">
                          <span className="label">Your Answer:</span>
                          <span className="answer-text">
                            {answer.userAnswer !== undefined && answer.userAnswer !== null
                              ? `Option ${String.fromCharCode(65 + answer.userAnswer)}`
                              : 'Not answered'}
                          </span>
                        </div>

                        {!isCorrect && (
                          <div className="correct-answer">
                            <span className="label">Correct Answer:</span>
                            <span className="answer-text">
                              Option {String.fromCharCode(65 + answer.correctAnswer)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Review Footer */}
            <div className="review-footer">
              <button className="btn-exit" onClick={handleExit}>
                ← Back to Tests
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PracticeTestResults;
